import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAwsRegions, fetchAwsCatalog } from '../store/actions/cluster.actions';
import { resolveStackImage, imageShapeCompatibility, softwareEntryFor } from './imageCompat';

/**
 * Live AWS data for the credential-first cluster form.
 *
 * Pick a credential -> the account's enabled regions are fetched.
 * Pick a region     -> zones (with the account's real AZ letters), the full
 *                      instance-type catalog, the per-zone offerings and
 *                      on-demand prices are fetched together.
 *
 * Everything the selectors need to make a bad combination unselectable is
 * derived here: `isOffered(zoneId, instanceId)` is the single source of
 * truth, `zoneOptionState` / `instanceOptionState` turn it into
 * { disabled, reason } for the dropdowns. All predicates answer `null`
 * ("don't know") until the catalog for the current credential + region has
 * actually arrived, so nothing is greyed out on stale or missing data.
 */

// describe-regions returns codes only; these are the display names AWS uses.
const AWS_REGION_NAMES = {
    'us-east-1': 'US East (N. Virginia)', 'us-east-2': 'US East (Ohio)',
    'us-west-1': 'US West (N. California)', 'us-west-2': 'US West (Oregon)',
    'us-gov-east-1': 'AWS GovCloud (US-East)', 'us-gov-west-1': 'AWS GovCloud (US-West)',
    'ca-central-1': 'Canada (Central)', 'ca-west-1': 'Canada West (Calgary)',
    'mx-central-1': 'Mexico (Central)', 'sa-east-1': 'South America (São Paulo)',
    'eu-central-1': 'Europe (Frankfurt)', 'eu-central-2': 'Europe (Zurich)',
    'eu-west-1': 'Europe (Ireland)', 'eu-west-2': 'Europe (London)', 'eu-west-3': 'Europe (Paris)',
    'eu-north-1': 'Europe (Stockholm)', 'eu-south-1': 'Europe (Milan)', 'eu-south-2': 'Europe (Spain)',
    'il-central-1': 'Israel (Tel Aviv)', 'me-central-1': 'Middle East (UAE)', 'me-south-1': 'Middle East (Bahrain)',
    'af-south-1': 'Africa (Cape Town)',
    'ap-east-1': 'Asia Pacific (Hong Kong)', 'ap-east-2': 'Asia Pacific (Taipei)',
    'ap-south-1': 'Asia Pacific (Mumbai)', 'ap-south-2': 'Asia Pacific (Hyderabad)',
    'ap-northeast-1': 'Asia Pacific (Tokyo)', 'ap-northeast-2': 'Asia Pacific (Seoul)', 'ap-northeast-3': 'Asia Pacific (Osaka)',
    'ap-southeast-1': 'Asia Pacific (Singapore)', 'ap-southeast-2': 'Asia Pacific (Sydney)',
    'ap-southeast-3': 'Asia Pacific (Jakarta)', 'ap-southeast-4': 'Asia Pacific (Melbourne)',
    'ap-southeast-5': 'Asia Pacific (Malaysia)', 'ap-southeast-7': 'Asia Pacific (Thailand)',
};

export const awsRegionLabel = (regionCode) => (
    AWS_REGION_NAMES[regionCode] ? `${regionCode} - ${AWS_REGION_NAMES[regionCode]}` : regionCode
);

/**
 * The geography a region belongs to, for the dropdown's headings. AWS has no
 * field for this (describe-regions returns codes only), but its own display
 * names start with it: "Europe (Frankfurt)", "Asia Pacific (Tokyo)".
 */
export const awsRegionGroup = (regionCode) => {
    const name = AWS_REGION_NAMES[regionCode];
    if (!name) return 'Other';
    if (name.startsWith('AWS GovCloud')) return 'US (GovCloud)';
    if (name.startsWith('US ')) return 'US';
    const head = name.split(' (')[0];
    // AWS names Israel on its own; geographically it belongs with the rest.
    return head === 'Israel' ? 'Middle East' : head;
};

const EMPTY = [];

// Shape the live catalog entry the way the shared InstanceSizeSelector already
// reads shapes (it probes vcpus / memory / gpus / gpu_type / architecture), so
// the selector needs no AWS-specific knowledge.
function toSelectorShape(shape, prices) {
    return {
        instance_id: shape.instance_id,
        name: shape.instance_id,
        vcpus: shape.vcpus,
        cores: String(shape.vcpus),
        memory: shape.memGiB,
        gpus: shape.gpu ? shape.gpu.count : undefined,
        gpu_type: shape.gpu ? shape.gpu.name : undefined,
        architecture: shape.arch,
        archs: shape.archs || [shape.arch],
        EFA_support: shape.efa,
        bareMetal: shape.bareMetal,
        storageGB: shape.storageGB,
        // What an image must look like to boot on this type (see imageCompat.js).
        virtTypes: shape.virtTypes || [],
        bootModes: shape.bootModes || [],
        rootDeviceTypes: shape.rootDeviceTypes || [],
        enaSupport: shape.enaSupport || 'unsupported',
        price: prices && prices[shape.instance_id] != null ? prices[shape.instance_id] : null,
    };
}

export default function useAwsCatalog({ credentialId, regionId, enabled = true }) {
    const dispatch = useDispatch();
    const regionsState = useSelector(({ clusters }) => clusters.cluster.awsCatalog.regions);
    const catalogState = useSelector(({ clusters }) => clusters.cluster.awsCatalog.catalog);

    // Fetch regions whenever the credential changes.
    useEffect(() => {
        if (!enabled || !credentialId) return;
        if (regionsState.credentialId === credentialId && (regionsState.loading || regionsState.list.length || regionsState.error)) return;
        dispatch(fetchAwsRegions(credentialId));
    }, [dispatch, enabled, credentialId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch the catalog whenever credential + region change.
    const requestKey = credentialId && regionId ? `${credentialId}|${regionId}` : null;
    useEffect(() => {
        if (!enabled || !requestKey) return;
        if (catalogState.requestKey === requestKey) return;
        dispatch(fetchAwsCatalog(credentialId, regionId));
    }, [dispatch, enabled, requestKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const retry = useCallback(() => {
        if (credentialId) dispatch(fetchAwsRegions(credentialId));
        if (credentialId && regionId) dispatch(fetchAwsCatalog(credentialId, regionId));
    }, [dispatch, credentialId, regionId]);

    // --- Regions ---------------------------------------------------------
    const regionsForCredential = regionsState.credentialId === credentialId;
    const regions = useMemo(() => (
        regionsForCredential
            ? regionsState.list.map((r) => ({
                region_id: r.regionCode,
                name: awsRegionLabel(r.regionCode),
                group: awsRegionGroup(r.regionCode),
            }))
            : EMPTY
    ), [regionsForCredential, regionsState.list]);

    // --- Catalog ---------------------------------------------------------
    const data = catalogState.requestKey === requestKey ? catalogState.data : null;
    const catalogReady = Boolean(requestKey && data && !catalogState.loading && !catalogState.error);
    const catalogLoading = Boolean(requestKey && catalogState.requestKey === requestKey && catalogState.loading);
    const catalogError = requestKey && catalogState.requestKey === requestKey ? catalogState.error : null;

    const zones = useMemo(() => (
        data ? data.zones.map((z) => ({ zone_id: z.zoneId, name: `${z.zoneName} (${z.zoneId})`, zoneName: z.zoneName })) : EMPTY
    ), [data]);

    const shapes = useMemo(() => (data ? data.shapes.map((s) => toSelectorShape(s, data.prices)) : EMPTY), [data]);
    const shapeById = useCallback((instanceId) => shapes.find((s) => s.instance_id === instanceId) || null, [shapes]);

    // Images the credential's account owns in this region (null = could not list).
    const images = data && Array.isArray(data.images) ? data.images : null;
    const imagesReady = catalogReady && Array.isArray(images);
    const imageById = useCallback((imageId) => (images ? images.find((i) => i.imageId === imageId) || null : null), [images]);
    const resolveImage = useCallback((imageName, kind) => (images ? resolveStackImage(images, imageName, kind) : null), [images]);

    // Marketplace software fees for product-coded images in this catalog.
    const softwarePricing = data ? data.softwarePricing || null : null;
    const softwareFor = useCallback((image) => softwareEntryFor(image, softwarePricing), [softwarePricing]);

    const offerings = data ? data.offerings : null;
    const zoneNameById = useMemo(() => {
        const map = {};
        zones.forEach((z) => { map[z.zone_id] = z.zoneName; });
        return map;
    }, [zones]);

    const zoneLabel = useCallback((zoneId) => (
        zoneNameById[zoneId] ? `${zoneNameById[zoneId]} (${zoneId})` : zoneId
    ), [zoneNameById]);

    // true / false when the catalog for this exact credential+region is here, else null.
    const isOffered = useCallback((zoneId, instanceId) => {
        if (!catalogReady || !zoneId || !instanceId) return null;
        const list = offerings[zoneId];
        if (!list) return null; // a zone AWS did not report: not our call
        return list.includes(instanceId);
    }, [catalogReady, offerings]);

    const zonesOffering = useCallback((instanceId) => {
        if (!catalogReady || !instanceId) return EMPTY;
        return Object.keys(offerings).filter((zoneId) => offerings[zoneId].includes(instanceId));
    }, [catalogReady, offerings]);

    const isOfferedInRegion = useCallback((instanceId) => {
        if (!catalogReady || !instanceId) return null;
        return zonesOffering(instanceId).length > 0;
    }, [catalogReady, zonesOffering]);

    // Zone dropdown row: disabled when it lacks any of the shapes that row is
    // responsible for (head shape + inheriting groups, or a group's own shape).
    const zoneOptionState = useCallback((zoneId, instanceIds) => {
        const missing = (instanceIds || []).filter((id) => id && isOffered(zoneId, id) === false);
        if (missing.length === 0) return { disabled: false, reason: null };
        return { disabled: true, reason: `Not offered: ${[...new Set(missing)].join(', ')}` };
    }, [isOffered]);

    // Shape dropdown row: disabled when the row's effective zone lacks it (or,
    // with no zone chosen yet, when no zone in the region offers it), or when
    // the chosen image cannot boot on it.
    const instanceOptionState = useCallback((instanceId, zoneId, image, licence) => {
        if (zoneId) {
            if (isOffered(zoneId, instanceId) === false) return { disabled: true, reason: `Not offered in ${zoneNameById[zoneId] || zoneId}` };
        } else if (isOfferedInRegion(instanceId) === false) {
            return { disabled: true, reason: `Not offered in ${regionId}` };
        }
        if (image) {
            const verdict = imageShapeCompatibility(image, shapeById(instanceId), licence);
            if (!verdict.compatible) return { disabled: true, reason: verdict.shapeReason };
        }
        return { disabled: false, reason: null };
    }, [isOffered, isOfferedInRegion, zoneNameById, regionId, shapeById]);

    const priceOf = useCallback((instanceId) => (
        data && data.prices && data.prices[instanceId] != null ? data.prices[instanceId] : null
    ), [data]);

    return useMemo(() => ({
        regions,
        regionsLoading: regionsForCredential && regionsState.loading,
        regionsError: regionsForCredential ? regionsState.error : null,
        zones, shapes,
        catalogReady, catalogLoading, catalogError,
        pricing: data ? data.pricing : null,
        priceOf,
        images, imagesReady, imageCatalog: data ? data.imageCatalog : null,
        imageById, resolveImage, shapeById,
        softwarePricing, softwareFor,
        isOffered, isOfferedInRegion, zonesOffering,
        zoneOptionState, instanceOptionState, zoneLabel, zoneNameById,
        retry,
    }), [regions, regionsForCredential, regionsState.loading, regionsState.error, zones, shapes,
        catalogReady, catalogLoading, catalogError, data, priceOf, images, imagesReady, imageById, resolveImage, shapeById, softwarePricing, softwareFor,
        isOffered, isOfferedInRegion, zonesOffering, zoneOptionState, instanceOptionState, zoneLabel, zoneNameById, retry]);
}
